import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import { changePassword } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

const initialCriteria = {
  length: false,
  uppercase: false,
  lowercase: false,
  number: false,
  specialChar: false,
};

export default function ChangePasswordDialog({ open, onClose, onSuccess, verified = true }) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordCriteria, setPasswordCriteria] = useState(initialCriteria);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    if (open) {
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowOld(false);
      setShowNew(false);
      setShowConfirm(false);
      setPasswordCriteria(initialCriteria);
      setErrorMessage('');
    }
  }, [open]);

  const updateCriteria = (value) => {
    setPasswordCriteria({
      length: value.length >= 8,
      uppercase: /[A-Z]/.test(value),
      lowercase: /[a-z]/.test(value),
      number: /[0-9]/.test(value),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(value),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!oldPassword || !newPassword || newPassword !== confirmPassword) return;
    const allMet =
      passwordCriteria.length &&
      passwordCriteria.uppercase &&
      passwordCriteria.lowercase &&
      passwordCriteria.number &&
      passwordCriteria.specialChar;
    if (!allMet) return;

    setSubmitting(true);
    try {
      await changePassword(oldPassword, newPassword);
      const msg = 'Password changed successfully. Log in again with the new password.';
      await logout();
      navigate('/sign-in', { state: { successMessage: msg } });
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || 'Failed to change password.');
    } finally {
      setSubmitting(false);
    }
  };

  const criteriaMet =
    passwordCriteria.length &&
    passwordCriteria.uppercase &&
    passwordCriteria.lowercase &&
    passwordCriteria.number &&
    passwordCriteria.specialChar;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{ component: 'form', onSubmit: handleSubmit }}
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {!verified && (
            <Alert severity="info">
              Please verify your email before changing password.
            </Alert>
          )}
          <div>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
              Old Password
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="••••••"
              disabled={!verified}
              type={showOld ? 'text' : 'password'}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowOld((v) => !v)}
                      sx={{
                        p: 0,
                        width: '1.5rem',
                        height: '1.5rem',
                        border: 0,
                        bgcolor: 'transparent',
                        '&:hover': { bgcolor: 'transparent' },
                        '&:active': { bgcolor: 'transparent' },
                      }}
                    >
                      {showOld ? (
                        <VisibilityOffRoundedIcon fontSize="small" />
                      ) : (
                        <VisibilityRoundedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
              New Password
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="••••••"
              disabled={!verified}
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                updateCriteria(e.target.value);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowNew((v) => !v)}
                      sx={{
                        p: 0,
                        width: '1.5rem',
                        height: '1.5rem',
                        border: 0,
                        bgcolor: 'transparent',
                        '&:hover': { bgcolor: 'transparent' },
                        '&:active': { bgcolor: 'transparent' },
                      }}
                    >
                      {showNew ? (
                        <VisibilityOffRoundedIcon fontSize="small" />
                      ) : (
                        <VisibilityRoundedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography component="div" variant="caption" sx={{ mt: 0.5 }}>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                <li style={{ color: passwordCriteria.length ? 'green' : 'red' }}>
                  At least 8 characters
                </li>
                <li style={{ color: passwordCriteria.uppercase ? 'green' : 'red' }}>
                  At least one uppercase letter
                </li>
                <li style={{ color: passwordCriteria.lowercase ? 'green' : 'red' }}>
                  At least one lowercase letter
                </li>
                <li style={{ color: passwordCriteria.number ? 'green' : 'red' }}>
                  At least one number
                </li>
                <li style={{ color: passwordCriteria.specialChar ? 'green' : 'red' }}>
                  At least one special character
                </li>
              </ul>
            </Typography>
          </div>
          <div>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
              Confirm New Password
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="••••••"
              disabled={!verified}
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword && confirmPassword !== newPassword}
              helperText={
                confirmPassword && confirmPassword !== newPassword
                  ? 'Passwords do not match.'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowConfirm((v) => !v)}
                      sx={{
                        p: 0,
                        width: '1.5rem',
                        height: '1.5rem',
                        border: 0,
                        bgcolor: 'transparent',
                        '&:hover': { bgcolor: 'transparent' },
                        '&:active': { bgcolor: 'transparent' },
                      }}
                    >
                      {showConfirm ? (
                        <VisibilityOffRoundedIcon fontSize="small" />
                      ) : (
                        <VisibilityRoundedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <OutlinedButton onClick={onClose} text="Cancel" size="small" color="primary" />
          <FilledButton
            type="submit"
            text="Change"
            size="small"
            color="primary"
            disabled={
              !verified ||
              submitting ||
              !oldPassword ||
              !newPassword ||
              !confirmPassword ||
              !criteriaMet ||
              confirmPassword !== newPassword
            }
          />
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage('')} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
